import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoryService } from '../services/api';
import { Table, Pagination, StatusToggle, PageHeader, useToast, Card, Select } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';

const CategoryManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, getPagePermissions } = useAuth();

  // Category Type is an IT-only split (Software / Hardware). BioMedical does
  // not use it, so neither the column nor its filter appear there.
  const usesCategoryType = (user?.department_code || 'IT') === 'IT';
  const permissions = getPagePermissions('/admin/categories');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30
  });

  const [filters, setFilters] = usePageState('categoryManager', {
    currentPage: 1,
    itemsPerPage: 30,
    searchTerm: '',
    categoryTypeFilter: '',
    startDate: '',
    endDate: '',
  }, ['/admin/categories']);
  const { currentPage, itemsPerPage, searchTerm, categoryTypeFilter, startDate, endDate } = filters;
  const setCurrentPage        = (v) => setFilters({ currentPage: v });
  const setItemsPerPage       = (v) => setFilters({ itemsPerPage: v });
  const setSearchTerm         = (v) => setFilters({ searchTerm: v });
  const setCategoryTypeFilter = (v) => setFilters({ categoryTypeFilter: v });
  const setStartDate          = (v) => setFilters({ startDate: v });
  const setEndDate            = (v) => setFilters({ endDate: v });

  useEffect(() => {
    setPageTitle('Categories');
  }, [setPageTitle]);

  useEffect(() => {
    fetchCategories();
  }, [currentPage, searchTerm, categoryTypeFilter, startDate, endDate, itemsPerPage]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: itemsPerPage };
      if (searchTerm) params.search = searchTerm;
      if (categoryTypeFilter) params.category_type = categoryTypeFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await categoryService.getAll(params);
      setCategories(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to fetch categories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryTypeFilterChange = (value) => {
    setCategoryTypeFilter(value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCategoryTypeFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleView = (category) => {
    navigate(`/admin/categories/view/${category.id}`);
  };

  const handleEdit = (category) => {
    navigate(`/admin/categories/edit/${category.id}`);
  };

  const handleAddCategory = () => {
    navigate('/admin/categories/create');
  };

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this category?');
    if (confirmed) {
      try {
        await categoryService.delete(id);
        toast.error('Category deleted successfully');
        fetchCategories();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete category');
        console.error(err);
      }
    }
  };

  const handleStatusToggle = async (category, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [category.id]: true }));
    
    try {
      await categoryService.updateStatus(category.id, newStatus);
      toast.success(`Category ${newStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Refresh the categories list
      await fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update category status');
      console.error(err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [category.id]: false }));
    }
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader 
          addButton={permissions.can_add ? {
            label: 'Add Category',
            onClick: handleAddCategory,
            variant: 'primary'
          } : null}
          filters={{
            search: {
              value: searchTerm,
              onChange: handleSearchChange,
              placeholder: 'Search categories...'
            },
            // dateRange: {
            //   startDate: startDate,
            //   endDate: endDate,
            //   onStartDateChange: handleStartDateChange,
            //   onEndDateChange: handleEndDateChange
            // },
            custom: usesCategoryType ? (
              <Select
                value={categoryTypeFilter}
                onChange={(e) => handleCategoryTypeFilterChange(e.target.value)}
                options={[
                  { value: '', label: 'All Types' },
                  { value: 'Software', label: 'Software' },
                  { value: 'Hardware', label: 'Hardware' }
                ]}
                placeholder="All Types"
                searchable={false}
                className="w-[140px]"
              />
            ) : null
          }}
        />
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
              columns={[
                { 
                  header: 'S.No', 
                  accessor: 'serial',
                  width: '80px',
                  render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
                },
                { 
                  header: 'Code', 
                  accessor: 'code',
                  width: '120px',
                  render: (value) => (
                    <span style={{ 
                      fontFamily: 'monospace', 
                      backgroundColor: 'var(--gray-100)', 
                      padding: '0.25rem 0.5rem', 
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      {value}
                    </span>
                  )
                },
                { 
                  header: 'Name', 
                  accessor: 'name',
                  width: '200px',
                  render: (value) => (
                    <span className="text-sm text-gray-900" title={value}>
                      {value && value.length > 30 ? `${value.substring(0, 30)}...` : value}
                    </span>
                  )
                },
                ...(usesCategoryType ? [{
                  header: 'Type',
                  accessor: 'category_type',
                  width: '120px',
                  render: (value) => (
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      value === 'Software'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {value || 'N/A'}
                    </span>
                  )
                }] : []),
                {
                  header: 'Created',
                  accessor: 'created_at',
                  width: '120px',
                  render: (value) => formatDateDDMMYYYY(value),
                },
                {
                  header: 'Status',
                  accessor: 'status',
                  width: '100px',
                  align: 'center',
                  render: (value, row) => (
                    <StatusToggle
                      status={value}
                      onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                      loading={updatingStatus[row.id]}
                      disabled={!permissions.can_edit}
                    />
                  )
                },
                {
                  header: 'Actions',
                  accessor: 'actions',
                  width: '120px',
                  align: 'center',
                  actions: [
                    ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                    ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
                  ]
                },
              ]}
              data={permissions.can_list ? categories : []}
              onRowClick={permissions.can_view ? handleView : undefined}
              emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : 'No categories found'}
              loading={loading}
            />
            
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
            
      </Card>
    </div>
  );
};

export default CategoryManager;
